import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import { X } from "lucide-react";
import axios from "axios";
import { getOTP, verifyOTP, fetchUserDetails } from "../services/apiServices";
import apiClient from "../utils/appClient";
import { useAuthStore } from "../authStore";
import { useCbomContext } from "../context/CbomContext";

const LoginForm: React.FC = () => {
  const { setIsAuthenticated } = useCbomContext();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [messageStyle, setMessageStyle] = useState("text-red-500");
  const [isLoading, setIsLoading] = useState<{
    sendCode: boolean;
    verify: boolean;
  }>({ sendCode: false, verify: false });
  const [showVerificationField, setShowVerificationField] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [timer, setTimer] = useState(30);

  const isButtonEnabled = email.trim();
  const isVerifyEnabled = verificationCode.trim().length > 0;

  let companyName: string | null = null;

  const navigate = useNavigate();
  // const { setResponses } = useAssessmentStore();

  useEffect(() => {
    if (showVerificationField && timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(countdown);
    }
  }, [showVerificationField, timer]);

  const handleSendCode = async () => {
    setIsLoading((prev) => ({ ...prev, sendCode: true }));
    setTimer(30); // Reset timer when resending the code
    setMessage("");
    try {
      let otpResponse;

      if (companyName) {
        otpResponse = await getOTP(email, null, companyName);
      } else {
        otpResponse = await getOTP(email);
      }

      // console.log(otpResponse);
      setShowVerificationField(true);
      setMessageStyle("text-green-500");
      setMessage(otpResponse.message); // Update the success or failure message
    } catch (error: any) {
      setMessageStyle("text-red-500");
      setMessage(
        error?.response?.data?.message ||
          "Unexpected error occurred, Please try again later."
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, sendCode: false }));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const res = await apiClient.get(`/api/users/${email}`);
      // const res = await axios.get(`http://localhost:5051/api/users/${email}`);
      console.log(res);
      if (res.status === 200) {
        if (res.data.company_name) {
          companyName = res.data.company_name;
        }
        handleSendCode();
      }
    } catch (error) {
      setMessageStyle("text-red-500");
      setMessage("User not found...");
    }
    // handleSendCode();
  };

  const handleVerify = async () => {
    setIsLoading((prev) => ({ ...prev, verify: true }));
    setMessage("");

    try {
      const otpValue = parseInt(verificationCode);
      const res = await verifyOTP(email, otpValue);
      // console.log(res);

      // console.log("Setting token to the local storage");
      localStorage.setItem("token", res.token);

      // console.log("Setting authentication to true");
      useAuthStore.getState().setIsAuthenticated(true);

      const userDetails = await fetchUserDetails();
      localStorage.setItem("userId", userDetails.user_id);
      // setResponses(userDetails.user_response || {});
      setIsAuthenticated(true);
      navigate("/");
    } catch (error: any) {
      setMessageStyle("text-red-500");
      setMessage(
        error?.response?.data?.message ||
          "Unexpected Error occured, Please try again later"
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, verify: false }));
    }
  };

  return (
    <div className="relative bg-white w-full max-h-[80vh] sm:w-[70%] lg:w-[50%] xl:w-[40%] flex flex-col rounded-xl shadow-lg mt-[7px] overflow-auto">
      {/* <div>
        <X
          onClick={onClose}
          className="w-[30px] h-[30px] ml-auto cursor-pointer"
        />
      </div> */}

      <div className="px-4 lg:px-2 mt-3">
        <h1 className="text-custom-blue7 text-xl sm:text-3xl font-bold text-center mb-3">
          Login to continue...
        </h1>
      </div>

      <form className="w-full space-y-4 px-4 pb-4" onSubmit={handleSubmit}>
        <div>
          <label className="block text-custom-blue7 font-normal text-base mb-3">
            Email address
          </label>
          <input
            type="email"
            name="Email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border rounded-xl border-gray-300 outline-none text-customDark text-sm sm:text-normal"
          />
        </div>

        {showVerificationField && (
          <div>
            <label className="block text-custom-blue7 font-normal sm:text-normal mb-1">
              Verification Code
            </label>
            <input
              type="text"
              name="verification code"
              placeholder="Enter verification code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="w-full px-3 py-2 border rounded-xl outline-none border-gray-300 text-darkBlue sm:text-normal"
            />
          </div>
        )}

        <p className={`${messageStyle} text-center leading-tight`}>{message}</p>

        <div className="text-center">
          {!showVerificationField ? (
            <button
              type="submit"
              disabled={!isButtonEnabled || isLoading.sendCode}
              className={`w-full p-2 sm:w-[60%] rounded-full font-semibold text-lg sm:text-normal flex justify-center items-center gap-2 mx-auto ${
                isButtonEnabled
                  ? "bg-custom-blue9 text-white"
                  : "bg-custom-blue8 text-custom-blue4"
              }`}
            >
              {isLoading.sendCode ? (
                <>
                  <span className="w-6">
                    <TailSpin color="white" height={20} />
                  </span>
                  Sending...
                </>
              ) : (
                "Send Verification Code"
              )}
            </button>
          ) : (
            <div className="flex flex-col md:flex-row md:h-[45px] justify-center gap-4">
              <button
                onClick={handleVerify}
                type="button"
                disabled={!isVerifyEnabled}
                className={`w-full p-[6px] md:w-[240px] text-lg sm:text-normal rounded-full font-semibold flex justify-center items-center gap-2 drop-shadow-md ${
                  isVerifyEnabled
                    ? "bg-custom-blue9 text-white"
                    : "bg-custom-blue8 text-custom-blue4"
                }`}
              >
                {isLoading.verify ? (
                  <>
                    <span className="w-6">
                      <TailSpin color="white" height={20} />
                    </span>
                    Verifying...
                  </>
                ) : (
                  "Verify Code"
                )}
              </button>
              <button
                type="button"
                disabled={timer > 0}
                onClick={handleSendCode}
                className={`w-full p-[6px] md:w-[240px] flex items-center justify-center text-lg sm:text-normal font-semibold rounded-full drop-shadow-md ${
                  timer === 0
                    ? "bg-custom-blue9 text-white"
                    : "bg-custom-blue8 text-custom-blue4"
                }`}
              >
                {timer > 0 ? `Resend code in ${timer}s` : "Resend Code"}
              </button>
            </div>
          )}
        </div>
      </form>

      {/* <div className="flex items-center justify-center gap-3 py-3">
        <p>Not registered?</p>
        <button
          onClick={handleShowSignup}
          className="bg-custom-primaryBlue text-white px-5 rounded-full py-1"
        >
          Sign up
        </button>
      </div> */}
    </div>
  );
};

export default LoginForm;
